<template>
	<PanelDraggable :data="data" :draggable="isDraggable">
		<div>

			<!-- HEADER -->
			<Button v-if="dashboards.editMode" size="small" severity="danger" class="dash-delete-btn" @click="remove()">
				<GlyphIcon :icon="GlyphIconName['bin']" color="#fff" size="18" />
			</Button>
			<div class="dash-header" @mouseover="mouseOverHeader(true)" @mouseout="mouseOverHeader(false)" :editMode="dashboards.editMode" :class="{'queue-yellow': commonState === CommonState.YELLOW, 'queue-red': commonState === CommonState.RED}">
				<GlyphIcon :icon="GlyphIconName['user-parents']" color="#fff" size="18" style="margin-top:2px" />
				<div class="dotdotdot" style="margin-left:6px;margin-top:1px">{{ queue.name }}<span v-if="collapsed">&nbsp;({{ queue.callers.length }})</span></div>
				<div v-show="!dashboards.editMode" class="flex flex-row items-start gap-1.5 justify-between">
					<ToggleSwitch  v-model="isMember" :disabled="isPermanentMember" size="small" @value-change="membershipChanged" style="margin-top:2px" />
					<Button size="small" variant="text" class="header-button" @click="toggleCollapse()">
						<GlyphIcon :icon="GlyphIconName['chevron-up']" size="19" class="transition-transform" :class="{ 'upside-down': collapsed }" />
					</Button>
				</div>
			</div>

			<!-- EDIT MODE -->
			<div v-if="dashboards.editMode" class="dash-settings-box">
				<div class="w-full mb-3">
					<div>{{ t('queue.queue') }}</div>
					<Select fluid v-model="settings._id" :options="availableQueues" @change="queueSelected(settings._id)" option-value="value" option-label="label" size="small" :placeholder="t('queue.selectAQueue')" append-to="self" />
				</div>

				<div class="w-full mb-3">
					<div>{{ t('queue.showSection') }}</div>
					<div class="flex flex-row justify-between gap-0.5">
						<div class="flex items-center gap-3">
							<ToggleSwitch v-model="settings.showCalls" size="small" />
							<div class="section-label">{{ t('queue.showCallsInQueue') }}</div>
						</div>
						<div class="flex items-center gap-3">
							<ToggleSwitch v-model="settings.showMembers" size="small" />
							<div class="section-label">{{ t('queue.showMembers') }}</div>
						</div>
						<div class="flex items-center gap-3">
							<ToggleSwitch v-model="settings.showStats" size="small" />
							<div class="section-label">{{ t('queue.showStatistics') }}</div>
						</div>
					</div>
				</div>

				<div class="w-full mb-3 flex gap-4">
					<div class="w-1/2">
						<div>{{ t('queue.numberOfCalls') }}</div>
						<InputNumber v-model="settings.maxCalls" showButtons buttonLayout="horizontal" size="small" :step="1" :min="1" :max="20" fluid :disabled="!settings.showCalls">
							<template #incrementicon>
								<span class="pi pi-plus" />
							</template>
							<template #decrementicon>
								<span class="pi pi-minus" />
							</template>
						</InputNumber>
						<div class="variable-info pt-3">{{ t('queue.numberOfCallsDescription') }}</div>
					</div>
					<div class="w-1/2">
						<div>{{ t('queue.numberOfMembers') }}</div>
						<InputNumber v-model="settings.maxMembers" showButtons buttonLayout="horizontal" size="small" :step="1" :min="1" :max="20" fluid :disabled="!settings.showMembers">
							<template #incrementicon>
								<span class="pi pi-plus" />
							</template>
							<template #decrementicon>
								<span class="pi pi-minus" />
							</template>
						</InputNumber>
						<div class="variable-info pt-3">{{ t('queue.numberOfMembersDescription') }}</div>
					</div>
				</div>

				<div class="w-full mb-3 flex gap-4" v-if="data.showCalls">
					<div class="w-1/2">
						<div>{{ t('queue.callVariableName') }}</div>
						<InputText size="small" maxlength="64" fluid v-model="settings.variable" @keypress="inputValidator($event, /[a-zA-Z0-9-_]/)" @paste="inputPasteValidator($event, /^[a-zA-Z0-9-_]+$/)" />
					</div>
					<div class="w-1/2">
						<div>{{ t('queue.callVariableLabel') }}</div>
						<InputText size="small" maxlength="32" fluid v-model="settings.variableName" />
					</div>
				</div>
				<div class="variable-info" v-if="data.showCalls">{{ t('queue.callVariableDescription') }}</div>

				<div v-if="data.showStats">{{ t('queue.statisticsElementsHeader') }}</div>
				<div v-if="data.showStats" class="w-full flex flex-wrap gap-1 mb-1 stats-edit-box">
					<div class="stats-edit-item" @click="removeStat(item)" v-for="item in settings.stats" :key="item" v-html="t('queue.stats.' + item)"></div>
					<div v-if="settings.stats.length === 0" class="w-full text-center italic text-sm text-muted-color p-1">{{ t('common.noneSelected') }}</div>
				</div>
				<Select v-if="data.showStats" :placeholder="t('queue.addStatistics')" v-model="statsPicker" :options="statsAvailable" size="small" option-value="value" option-label="label" @change="addStat" fluid :disabled="statsAvailable.length === 0" append-to="self" />
			</div>

			<!-- VIEW MODE -->
			<div v-if="!dashboards.editMode && data._id" class="view-box" :style="{'max-height': (collapsed? '0px' : '2000px')}">

				<CallDraggable :data="queue" :type="CallDragTargetType.QueueCallers" :target="true" :draggable="false" :targetTypes="[CallDragItemType.MyCallsCall, CallDragItemType.QueueCall]">
					<div v-if="data.showCalls">
						<div class="section-header">
							<div>
								{{ t('queue.queueSectionHeader') }}
								<span v-if="collapsedCallers || queue.callers.length > settings.maxCalls">&nbsp;({{ queue.callers.length }})</span>
							</div>
							<div>
								<Button size="small" variant="text" class="subheader-button" @click="toggleCollapseCallers()">
									<GlyphIcon :icon="GlyphIconName['chevron-up']" size="15" class="transition-transform" :class="{'upside-down': collapsedCallers}" />
								</Button>
							</div>
						</div>
						<div class="vertical-scroll" style="transition:500ms" :style="{'max-height': (collapsedCallers ? 0 : data.maxCalls*45 + 10)+'px','min-height': (collapsedCallers ? 0 : data.maxCalls*45 + 10)+'px'}">
							<transition-group name="list2" tag="div">
								<div v-for="(call, index) in queue.callers" :key="call._id" class="list-item">
									<QueueCall :call="call" :queueId="queue._id" :index="index" :variable="data.variable" :variableName="data.variableName" :last="queue.callers.length-1==index" :serviceLevel="queue.serviceLevel" :serviceLevelCritical="queue.serviceLevelCritical" />
								</div>
							</transition-group>
						</div>
					</div>
				</CallDraggable>

				<CallDraggable :data="queue" :type="CallDragTargetType.QueueMembers" :target="true" :draggable="false" :targetTypes="[CallDragItemType.Employee, CallDragItemType.QueueMember]">
					<div v-if="data.showMembers">
						<div class="section-header">
							<div>
								{{ t('queue.memberSectionHeader') }}
								<span v-if="collapsedMembers || queue.members.length > settings.maxMembers">&nbsp;({{ queue.members.length }})</span>
							</div>
							<div class="flex items-center gap-1">
								<Button size="small" variant="text" class="header-button" @click="addMembersMenu?.toggle($event)" v-if="canManage">
									<GlyphIcon :icon="GlyphIconName['circle-empty-plus']" size="15" />
								</Button>
								<Button size="small" variant="text" class="header-button" @click="toggleCollapseMembers()">
									<GlyphIcon :icon="GlyphIconName['chevron-up']" class="transition-transform" :class="{ 'upside-down': collapsedMembers }" size="15" />
								</Button>
							</div>
						</div>
						<div class="vertical-scroll" style="transition:500ms" :style="{'max-height': (collapsedMembers ? 0 : data.maxMembers*42 + 20)+'px','min-height': (collapsedMembers ? 0 : data.maxMembers*42 + 20)+'px'}">
							<transition-group name="list2" tag="div">
								<div v-for="(member, index) in orderedMembers" :key="member._id" class="list-item">
									<QueueMember :member="member" :index="index" :queueId="queue._id" :last="queue.members.length-1==index" />
								</div>
							</transition-group>
						</div>
						<Menu ref="addMembersMenu" :model="addMembersMenuItems" :popup="true" />
					</div>
				</CallDraggable>

				<div v-if="data.showStats">
					<div class="section-header">
						<div>
							{{ t('queue.statisticsSectionHeader') }}
							<span v-if="collapsedStatistics">&nbsp;({{data.stats.length }})</span>
						</div>
						<div>
							<Button size="small" variant="text" class="header-button" @click="toggleCollapseStatistics()">
								<GlyphIcon :icon="GlyphIconName['chevron-up']" class="transition-transform" :class="{ 'upside-down': collapsedStatistics }" size="15" />
							</Button>
						</div>
					</div>
					<div class="stats-box" style="transition:500ms" :style="{'max-height': (collapsedStatistics ? '0px' : statsHeight)}">
						<div v-for="key in data.stats" :key="key" :class="{'clickable': clickableStats.includes(key)}" @click="statsClicked(key)">
							<div v-if="key=='NOMEMBERS'" class="stats-head" v-html="t('queue.stats.NOMEMBERS')"></div>
							<div v-if="key=='ALLBUSY'" class="stats-head" v-html="t('queue.stats.ALLBUSY')"></div>
							<div v-if="key=='TIMEOUT'" class="stats-head">{{ t('queue.stats.TIMEOUT1') }}<br>{{ ss.settings.dashboardColumnWidth < 370 ? '' : t('queue.stats.TIMEOUT2') }} {{ t('queue.stats.TIMEOUT3') }}</div>
							<div v-if="key=='CANCELLED'" class="stats-head" v-html="t('queue.stats.CANCELLED')"></div>
							<div v-if="key=='ANSWERED'" class="stats-head" v-html="t('queue.stats.ANSWERED')"></div>
							<div v-if="key=='UNANSWERED'" class="stats-head" v-html="t('queue.stats.UNANSWERED')"></div>
							<div v-if="key=='TOTAL'" class="stats-head" v-html="t('queue.stats.TOTAL')"></div>
							<div v-if="key=='AVERAGEWAITTIME'" class="stats-head" v-html="t('queue.stats.AVERAGEWAITTIME')"></div>
							<div v-if="key=='AVERAGETALKTIME'" class="stats-head" v-html="t('queue.stats.AVERAGETALKTIME')"></div>
							<div v-if="key=='TOTALTALKTIME'" class="stats-head" v-html="t('queue.stats.TOTALTALKTIME')"></div>
							<div v-if="key=='LONGESTWAITTIME'" class="stats-head" v-html="t('queue.stats.LONGESTWAITTIME')"></div>
							<div v-if="key=='LASTWAITTIME'" class="stats-head" v-html="t('queue.stats.LASTWAITTIME')"></div>
							<div v-if="key=='SERVICELEVEL'" class="stats-head" v-html="t('queue.stats.SERVICELEVEL')"></div>
							<div v-if="key=='SERVICELEVELCRITICAL'" class="stats-head">{{ (ss.settings.dashboardColumnWidth < 370 ? t('queue.stats.SERVICELEVELCRITICAL1') : t('queue.stats.SERVICELEVELCRITICAL2')) }}<br>{{ t('queue.stats.SERVICELEVELCRITICAL3') }}</div>

							<div v-if="key=='NOMEMBERS'" class="stats-value">{{ queueStats.noMembers }}</div>
							<div v-if="key=='ALLBUSY'" class="stats-value">{{ queueStats.allBusy }}</div>
							<div v-if="key=='TIMEOUT'" class="stats-value">{{ queueStats.timeout }}</div>
							<div v-if="key=='CANCELLED'" class="stats-value">{{ queueStats.cancelled }}</div>
							<div v-if="key=='ANSWERED'" class="stats-value">{{ queueStats.answered }}</div>
							<div v-if="key=='UNANSWERED'" class="stats-value">{{ queueStats.unanswered }}</div>
							<div v-if="key=='TOTAL'" class="stats-value">{{ queueStats.total }}</div>
							<div v-if="key=='AVERAGEWAITTIME'" class="stats-value">{{ shortduration(queueStats.averageWaitTime, false) }}<span class="stats-value-label">{{ shortduration(queueStats.averageWaitTime, true) }}</span></div>
							<div v-if="key=='AVERAGETALKTIME'" class="stats-value">{{ shortduration(queueStats.averageTalkTime, false) }}<span class="stats-value-label">{{ shortduration(queueStats.averageTalkTime, true) }}</span></div>
							<div v-if="key=='TOTALTALKTIME'" class="stats-value">{{ shortduration(queueStats.totalTalkTime, false) }}<span class="stats-value-label">{{ shortduration(queueStats.totalTalkTime, true) }}</span></div>
							<div v-if="key=='LONGESTWAITTIME'" class="stats-value">{{ shortduration(queueStats.longestWaitTime, false) }}<span class="stats-value-label">{{ shortduration(queueStats.longestWaitTime, true) }}</span></div>
							<div v-if="key=='LASTWAITTIME'" class="stats-value">{{ shortduration(queueStats.lastWaitTime, false) }}<span class="stats-value-label">{{ shortduration(queueStats.lastWaitTime, true) }}</span></div>
							<div v-if="key=='SERVICELEVEL'" class="stats-value">{{ queueStats.serviceLevel }}<span class="stats-value-label">%</span></div>
							<div v-if="key=='SERVICELEVELCRITICAL'" class="stats-value">{{ queueStats.serviceLevelCritical }}<span class="stats-value-label">%</span></div>
						</div>
					</div>
				</div>

			</div>
			<QueueAddExternalModal v-if="queue" ref="addExternalModal" :queue="queue" />
			<QueueAddExtensionModal v-if="queue" ref="addExtensionModal" :queue="queue" />
			<QueueCallsModal v-if="queue" ref="queueCallsModal" :queue="queue" />
		</div>
	</PanelDraggable>
</template>

<script setup lang="ts">
import { useSettingsStore } from '@/stores/Settings'
import { useMediaDevicesStore } from '@/stores/MediaDevices'
import { useDashboardsStore } from '@/stores/Dashboards'
import { useEmployeesStore } from '@/stores/Employees'
import { useQueuesStore } from '@/stores/Queues'
import { useApiStore } from '@/stores/Api'
import { useI18n } from 'vue-i18n'
import globalEvents from '@/utilities/GlobalEvents'
import GlyphIcon from '@/components/GlyphIcon.vue'
import PanelDraggable from '@/components/dashboard/PanelDraggable.vue'
import QueueCall from '@/components/dashboard/QueueCall.vue'
import QueueMember from '@/components/dashboard/QueueMember.vue'
import QueueAddExternalModal from '@/components/modals/QueueAddExternalModal.vue'
import QueueAddExtensionModal from '@/components/modals/QueueAddExtensionModal.vue'
import QueueCallsModal from '@/components/modals/QueueCallsModal.vue'
import CallDraggable from '@/components/dashboard/CallDraggable.vue'
import { PbxAppDashboardItemQueueStats, PbxAppQueuesCallerState, type PbxAppDashboardItemQueue, type PbxAppQueuesAllItem, type PbxAppQueuesCaller, type PbxAppQueuesMember } from '@/TcxApi/PbxApp'
import { computed, onUnmounted, ref, watch } from 'vue'
import { type PbxQueueStatsResponse } from '@/TcxApi/PbxQueue'
import { dynamicSort } from '@/utilities/Tools'
import { EmployeeAccessLevel } from '@per_moeller/tcx-realtime-client'
import { GlyphIconName } from '@/stores/GlyphIcons.types'
import type { SelectChangeEvent } from 'primevue/select'
import { CallDragItemType, CallDragTargetType } from '@/utilities/CallDrag'
import type Menu from 'primevue/menu'
import type { MenuItem } from 'primevue/menuitem'
import { shortduration } from '@/utilities/filters'

const ss = useSettingsStore()
const mediaDevices = useMediaDevicesStore()
const dashboards = useDashboardsStore()
const employees = useEmployeesStore()
const queues = useQueuesStore()
const api = useApiStore()
const { t } = useI18n()

interface Props {
	data: PbxAppDashboardItemQueue
}

enum CommonState {
	GREEN = 'GREEN',
	YELLOW = 'YELLOW',
	RED = 'RED'
}

const props = defineProps<Props>()
const settings = dashboards.dashboard?.items.find(item => item === props.data) as PbxAppDashboardItemQueue
const isDraggable = ref(false)
const queue = ref<PbxAppQueuesAllItem>({ _id: '', name: 'ny kø', dynamic: false, member: false, serviceLevel: 0, serviceLevelCritical: 0, priority: 1, members: [], callers: [] })
const queueStats = ref<Omit<PbxQueueStatsResponse, '_id' | 'name' | 'date'>>({ noMembers: 0, allBusy: 0, timeout: 0, cancelled: 0, answered: 0, unanswered: 0, total: 0, averageWaitTime: 0, averageTalkTime: 0, totalTalkTime: 0, longestWaitTime: 0, lastWaitTime: 0, serviceLevel: 0, serviceLevelCritical: 0 })
const availableQueues = ref<{ value: string, label: string, name: string }[]>([])
const commonState = ref<CommonState>(CommonState.GREEN)
const isMember = ref(false) // If the user is a member of the queue
const isPermanentMember = ref(false) // If the user is a permanent member of the queue
const initRunning = ref(false)
const collapsed = ref<boolean>(ss.getGeneric(`${ss.settings.dashboardId}-${props.data.type}-${props.data.column}-${props.data.position}-collapsed`) || false)
const collapsedCallers = ref<boolean>(ss.getGeneric(`${ss.settings.dashboardId}-${props.data.type}-${props.data.column}-${props.data.position}-collapsedCallers`) || false)
const collapsedMembers = ref<boolean>(ss.getGeneric(`${ss.settings.dashboardId}-${props.data.type}-${props.data.column}-${props.data.position}-collapsedMembers`) || false)
const collapsedStatistics = ref<boolean>(ss.getGeneric(`${ss.settings.dashboardId}-${props.data.type}-${props.data.column}-${props.data.position}-collapsedStatistics`) || false)
const addExternalModal = ref<InstanceType<typeof QueueAddExternalModal>>()
const queueCallsModal = ref<InstanceType<typeof QueueCallsModal>>()
const addExtensionModal = ref<InstanceType<typeof QueueAddExtensionModal>>()
const statsPicker = ref<PbxAppDashboardItemQueueStats | null>(null)

const clickableStats = [ 'UNANSWERED', 'ANSWERED', 'NOMEMBERS', 'ALLBUSY', 'TIMEOUT', 'CANCELLED']

const addMembersMenuItems: MenuItem[] = [
	{ label: t('queue.addExtension'), icon: 'pi pi-user', command: addMembersExtensions },
	{ label: t('queue.addExternal'), icon: 'pi pi-phone', command: addMembersExternal }
]
const addMembersMenu = ref<InstanceType<typeof Menu>>()

const orderedMembers = computed(() => {
	return [ ...queue.value.members ].sort(dynamicSort('priority'))
})

const canManage = computed(() => {
	let can = false

	if (employees.me) {
		can = (!!queue.value._id && employees.me.permissions.queueMember)
	}

	if (queue.value._id && ss.isAuthenticated && ss.auth) {
		if (!queue.value.dynamic && [EmployeeAccessLevel.PERSONAL, EmployeeAccessLevel.VIEWER].includes(ss.auth.accessLevel)) {
			can = false
		}
	}
	return can
})

const statsHeight = computed(() => {
	if (props.data.showStats) {
		const rows = Math.ceil(props.data.stats.length / 4)

		return (rows * 56).toString() + 'px'
	} else {
		return '0px'
	}
})

const statsAvailable = computed(() => {
	const available: { value: PbxAppDashboardItemQueueStats, label: string }[] = []

	for (const key in PbxAppDashboardItemQueueStats) {
		if (!settings.stats.includes(key as PbxAppDashboardItemQueueStats)) {
			available.push({ value: key as PbxAppDashboardItemQueueStats, label: t('queue.stats2.' + key) })
		}
	}

	return available
})

// #region EDIT functions

function addStat(event: SelectChangeEvent) {
	settings.stats.push(event.value as PbxAppDashboardItemQueueStats)
	statsPicker.value = null
}

function removeStat(item: PbxAppDashboardItemQueueStats) {
	const index = settings.stats.indexOf(item)
	if (index > -1) {
		settings.stats.splice(index, 1)
	}
}

function remove() {
	dashboards.removeItem(props.data.column, props.data.position)
}

function mouseOverHeader(itIs: boolean) {
	if (itIs && dashboards.editMode && !dashboards.dragItem) {
		isDraggable.value = true
	} else {
		isDraggable.value = false
	}
}

function queueSelected(id: string) {
	queue.value = queues.getQueue(id) as PbxAppQueuesAllItem
}

function inputValidator(event: KeyboardEvent, rx: RegExp) {
	if (event.key.length === 1 && !rx.test(event.key)) {
		event.preventDefault()
	}
}

function inputPasteValidator(event: ClipboardEvent, rx: RegExp) {
	const clipboardData = event.clipboardData?.getData('text/plain')
	if (clipboardData && !rx.test(clipboardData)) {
		event.preventDefault()
	}
}

// #endregion

// #region VIEW functions

function toggleCollapse() {
	collapsed.value = !collapsed.value
	ss.setGeneric(`${ss.settings.dashboardId}-${props.data.type}-${props.data.column}-${props.data.position}-collapsed`, collapsed.value)
}

function toggleCollapseCallers() {
	collapsedCallers.value = !collapsedCallers.value
	ss.setGeneric(`${ss.settings.dashboardId}-${props.data.type}-${props.data.column}-${props.data.position}-collapsedCallers`, collapsedCallers.value)
}

function toggleCollapseMembers() {
	collapsedMembers.value = !collapsedMembers.value
	ss.setGeneric(`${ss.settings.dashboardId}-${props.data.type}-${props.data.column}-${props.data.position}-collapsedMembers`, collapsedMembers.value)
}

function toggleCollapseStatistics() {
	collapsedStatistics.value = !collapsedStatistics.value
	ss.setGeneric(`${ss.settings.dashboardId}-${props.data.type}-${props.data.column}-${props.data.position}-collapsedStatistics`, collapsedStatistics.value)
}

async function eventQueueDeleted(deletedQueue: PbxAppQueuesAllItem) {
	if (queue.value._id === deletedQueue._id) {
		dashboards.removeItem(props.data.column, props.data.position)

		try {
			await dashboards.save()
			globalEvents.emit('CommonErrorModal', { header: t('queue.panelDeletedHeader'), message: t('queue.panelDeletedMessage').replace('%%name%%', deletedQueue.name) })
		}
		catch (_) { /** Ignore */ }
	}
}

function eventQueueLeft(args: { queue: string, caller: PbxAppQueuesCaller, index: number }) {
	if (queue.value._id === args.queue) {
		if (args.caller.state === PbxAppQueuesCallerState.ANSWERED) {
			const waittime = Math.floor((new Date(args.caller.answered as Date).getTime() - new Date(args.caller.entered).getTime()) / 1000)

			queueStats.value.lastWaitTime = waittime
			queueStats.value.answered++
		} else if (args.caller.state === PbxAppQueuesCallerState.CANCELLED) {
			queueStats.value.cancelled++
			queueStats.value.unanswered++
		} else if (args.caller.state === PbxAppQueuesCallerState.TIMED_OUT) {
			queueStats.value.timeout++
			queueStats.value.unanswered++
		} else if (args.caller.state === PbxAppQueuesCallerState.NO_MEMBERS) {
			queueStats.value.noMembers++
			queueStats.value.unanswered++
		} else if (args.caller.state === PbxAppQueuesCallerState.ALL_BUSY) {
			queueStats.value.allBusy++
			queueStats.value.unanswered++
		}
		queueStats.value.total++
	}
}

function eventQueueMemberJoin(args: { queue: string, member: PbxAppQueuesMember }) { // args = { queue: id, member: { _id, extension, number, priority, calls, lastCallEnded, permanent }}
	if (queue.value._id == args.queue) {
		if (employees.me?.extensions.map(ext => ext._id).includes(args.member.extension as string)) {
			isMember.value = true
			isPermanentMember.value = args.member.permanent || false
		}
	}
}

function eventQueueMemberUpdate(args: { queue: string, member: PbxAppQueuesMember, index: number }) { // args = { queue: id, member: { _id, extension, number, priority, calls, lastCallEnded, permanent }, index: number }
	if (queue.value._id == args.queue) {
		if (employees.me?.extensions.map(ext => ext._id).includes(args.member.extension as string)) {
			isPermanentMember.value = args.member.permanent || false
		}
	}
}

function eventQueueMemberLeft(args: { queue: string, member: PbxAppQueuesMember, index: number }) { // args = { queue: id, member: { _id, extension, number, priority, calls, lastCallEnded, permanent }, index: number }
	if (queue.value._id === args.queue) {
		if (employees.me?.extensions.map(ext => ext._id).includes(args.member.extension as string)) {
			isMember.value = false
			isPermanentMember.value = false
		}
	}
}

function addMembersExtensions() {
	addExtensionModal.value?.show()
}

function addMembersExternal() {
	addExternalModal.value?.show()
}

async function membershipChanged(newValue: boolean) {
	if (newValue) {
		try { await api.post(`/pbx/queue/${queue.value._id}/member`, { extension: employees.me?.primaryExtension?._id }) }
		catch (_) { isMember.value = false }
	} else {
		// Ensure I am not a permanent member
		const member = queue.value.members.find(member => member.extension == employees.me?.primaryExtension?._id)
		if (member && !member.permanent) {
			try { await api.del(`/pbx/queue/${queue.value._id}/member/${employees.me?.primaryExtension?._id}`) }
			catch (_) { isMember.value = true }
		}
	}
}

function statsClicked(key: PbxAppDashboardItemQueueStats) {
	if (!clickableStats.includes(key)) { return }
	queueCallsModal.value?.show(key)
}

async function loadStats() {
	if (props.data.showStats) {
		try {
			const res = await api.get<PbxQueueStatsResponse>(`/pbx/queue/${props.data._id}/stats`)

			queueStats.value = res.data
		}
		catch (_) { /** Ignore */ }
	}
}

function updateCommonState() {
	let color: CommonState = CommonState.GREEN

	for (const call of queue.value.callers) {
		const seconds = Math.round((new Date().getTime() - call.entered.getTime()) / 1000)

		if (seconds > queue.value.serviceLevel && seconds <= queue.value.serviceLevelCritical) {
			color = CommonState.YELLOW
		} else if (seconds > queue.value.serviceLevelCritical) {
			color = CommonState.RED
		}
	}

	if (commonState.value !== color) {
		commonState.value = color
		if (color !== CommonState.GREEN && ss.settings.queueCriticalSound) {
			playCommonStateSound()
		}
	}
}

async function playCommonStateSound() {
	const parent = document.getElementById('SipPhoneVideoContainer')
	const ae = document.createElement('audio')

	if (parent) {
		ae.id = 'RingTonePlayer'
		ae.autoplay = true
		ae.loop = false
		parent.appendChild(ae)
		if (ss.settings.media.preferredRingerOutputs[0]) {
			await ae.setSinkId(ss.settings.media.preferredRingerOutputs[0])
		}
		ae.src = 'https://audio.telecomx.dk/619e5328aa69b00a66f57f6a.mp3' // Critical sound - backup via MongoDB:CommonFiles
		ae.currentTime = 0
		ae.volume = mediaDevices.ringerVolume
		ae.play()
		setTimeout(() => {
			parent.removeChild(ae)
			ae.src = ''
		}, 7000)
	}
}

// #endregion

// Initialize

async function init() {
	await employees.ready()

	if (initRunning.value) { return }
	initRunning.value = true

	if (props.data._id) { // Load queue, if selected
		queue.value = queues.getQueue(props.data._id) as PbxAppQueuesAllItem
		if (!queue.value && queues.isReady) {
			globalEvents.emit('Dashboard:DeleteItem', props.data)
		}
	}

	if (queue.value) {
		loadStats()
		const extensionIds = employees.me?.extensions.map(o => o._id) || []
		const member = queue.value.members.find(member => extensionIds.includes(member._id))

		isMember.value = !!member
		isPermanentMember.value = member?.permanent || false
	}

	if (props.data.showStats) {
		if (!props.data.stats || props.data.stats.length === 0) {
			settings.stats = [ PbxAppDashboardItemQueueStats.UNANSWERED, PbxAppDashboardItemQueueStats.ANSWERED, PbxAppDashboardItemQueueStats.AVERAGEWAITTIME, PbxAppDashboardItemQueueStats.LASTWAITTIME]
		}
	}

	initRunning.value = false
}

init()

if (dashboards.editMode) {
	availableQueues.value.push(...queues.list)
}

globalEvents.on('Queues:Deleted', eventQueueDeleted)
globalEvents.on('Queues:Left', eventQueueLeft)
globalEvents.on('Queues:MemberJoin', eventQueueMemberJoin)
globalEvents.on('Queues:MemberUpdate', eventQueueMemberUpdate)
globalEvents.on('Queues:MemberLeft', eventQueueMemberLeft)
globalEvents.on('Queues:Loaded', init)
globalEvents.on('OneSecond', updateCommonState)
globalEvents.on('OneMinute', loadStats)

onUnmounted(() => {
	initRunning.value = true // ensures init is not called after unmount
	globalEvents.off('Queues:Deleted', eventQueueDeleted)
	globalEvents.off('Queues:Left', eventQueueLeft)
	globalEvents.off('Queues:MemberJoin', eventQueueMemberJoin)
	globalEvents.off('Queues:MemberUpdate', eventQueueMemberUpdate)
	globalEvents.off('Queues:MemberLeft', eventQueueMemberLeft)
	globalEvents.off('Queues:Loaded', init)
	globalEvents.off('OneSecond', updateCommonState)
	globalEvents.off('OneMinute', loadStats)
})

// When edit mode is enabled, load the list of available queues
watch(() => dashboards.editMode, (newValue) => {
	if (newValue) {
		availableQueues.value = []
		availableQueues.value.push(...queues.list)
		availableQueues.value.sort(dynamicSort('name'))
	} else {
		availableQueues.value = []
		init()
	}
})
</script>

<style scoped>
	.section-header {
		border-radius: 5px;
		background-color: var(--bg-color-dashboard-header);
		color: var(--txt-color-dashboard-header);
		padding-left: 10px;
		padding-right: 2px;
		margin: 4px;
		font-size: 12px;
		line-height: 13px;
		font-weight: 300;
		display: flex;
		justify-content: space-between;
		align-items: center;
	}
	.settings-box {
		margin: 5px 5px;
	}
	.view-box {
		/* margin: 5px 5px; */
		overflow: auto;
		transition: 500ms;
	}
	.list-item {
		display: inline-block;
		transition: all 0.7s;
		width: 100%;
	}

	/* ENTER */
	.list2-enter-from {
	opacity: 0;
	transform: translateY(15px);
	}
	.list2-enter-active {
	transition: opacity 0.3s ease, transform 0.3s ease;
	}
	.list2-enter-to {
	opacity: 1;
	transform: translateY(0);
	}

	/* LEAVE */
	.list2-leave-from {
	opacity: 1;
	transform: translateY(0);
	}
	.list2-leave-active {
	transition: opacity 0.3s ease, transform 0.3s ease;
	}
	.list2-leave-to {
	opacity: 0;
	transform: translateY(15px);
	}

	.vertical-scroll {
		overflow-y: scroll;
		overflow-x: hidden;
		margin-bottom: 5px;
	}
	.vertical-scroll::-webkit-scrollbar {
		width: 1px;
		background: transparent;
	}
	.vertical-scroll::-webkit-scrollbar-thumb {
		background: var(--pn-primary-1);
	}
	.add-member {
		margin-top: -1px;
		margin-left: 5px;
		cursor: pointer;
	}
	.stats-box {
		display: grid;
		grid-template-columns: 1fr 1fr 1fr 1fr;
		column-gap: 6px;
		margin-left: 4px;
		margin-right: 4px;
		overflow: hidden;
	}
	.stats-box > div {
		border-radius: 10px;
		background-image: radial-gradient(circle at left top, var(--p-surface-50), var(--p-surface-200));
		box-shadow: 0px 0px 3px 0px #66666640;
		padding-top: 5px;
		padding-bottom: 4px;
		margin-bottom: 5px;
		border: 1px solid var(--p-form-field-border-color);
	}
	.stats-head {
		font-size: 10px;
		text-align: center;
		line-height: 10px;
		color: var(--p-text-color);
		padding-bottom: 2px;
	}
	.stats-value {
		font-size: 16px;
		line-height: 16px;
		color: #BBAC91;
		font-weight: 600;
		text-align: center;
	}
	.stats-value-label {
		font-size: 11px;
		color: var(--p-text-color);
		font-weight: 300;
		margin-left:2px;
	}
	@media(prefers-color-scheme: dark) {
		.stats-box > div {
			background-image: radial-gradient(circle at left top, var(--p-surface-800), var(--p-surface-950));
			box-shadow: 0px 0px 3px 0px #ffffff40;
			border: 1px solid #333333;
		}
		.stats-head {
			color: var(--p-text-muted-color);
		}
		.stats-value-label {
			color: var(--p-text-muted-color);
		}
	}
	.variable-info {
		margin-top: -7px;
		line-height: 12px;
		font-size: 11px;
		margin-bottom:10px;
		color: var(--p-text-muted-color);
	}
	.section-label {
		font-size: 12px;
		margin-left: -7px;
	}
	.clickable {
		cursor: pointer;
	}
	.stats-edit-box {
		border: 1px solid var(--p-form-field-border-color);
		border-radius: 5px;
		background-color: var(--p-form-field-background);
		padding: 2px;
	}
	.stats-edit-item {
		width: 25%;
		cursor: s-resize;
		flex: 1 0 22%;
		font-size: 10px;
		text-align: center;
		line-height: 11px;
		padding: 4px 1px;
		background-color: var(--p-surface-200);
		border: 1px solid var(--p-form-field-border-color);
		border-radius: 5px 5px;
	}
	@media(prefers-color-scheme: dark) {
		.stats-edit-item {
			background-color: var(--p-surface-950);
		}
	}
</style>
